import { ContractStatusEnum, MilestoneStatusEnum } from "../modules/shared/model";
import { isContractResponseTimeExpired } from "./categoryFormSettingsUtils";

/**
 * Get contract status text based on status ID
 */
export const getContractStatusText = (statusId: number): string => {
  switch (Number(statusId)) {
    case ContractStatusEnum.Pending: return "Pending";
    case ContractStatusEnum.Active: return "Active";
    case ContractStatusEnum.Cancelled: return "Cancelled";
    case ContractStatusEnum.Completed: return "Completed";
    case ContractStatusEnum.Archived: return "Archived";
    case ContractStatusEnum.Suspended: return "Suspended";
    case ContractStatusEnum.Deleted: return "Deleted";
    case ContractStatusEnum.Paused: return "Paused";
    case ContractStatusEnum.Expired: return "Expired";
    case ContractStatusEnum.Rejected: return "Rejected";
    case ContractStatusEnum.Accepted: return "Accepted";
    case ContractStatusEnum.Closed: return "Completed";
    default: return "Unknown";
  }
};

/**
 * Get contract status color classes based on status ID
 */
export const getContractStatusColor = (statusId: number): string => {
  switch (Number(statusId)) {
    case ContractStatusEnum.Pending: return "bg-yellow-100 text-yellow-800";
    case ContractStatusEnum.Active: return "bg-blue-100 text-blue-800";
    case ContractStatusEnum.Cancelled: return "bg-red-100 text-red-800";
    case ContractStatusEnum.Completed: return "bg-green-100 text-green-800";
    case ContractStatusEnum.Archived: return "bg-gray-100 text-gray-800";
    case ContractStatusEnum.Suspended: return "bg-orange-100 text-orange-800";
    case ContractStatusEnum.Deleted: return "bg-red-100 text-red-800";
    case ContractStatusEnum.Paused: return "bg-yellow-100 text-yellow-800";
    case ContractStatusEnum.Expired: return "bg-red-100 text-red-800";
    case ContractStatusEnum.Rejected: return "bg-red-100 text-red-800";
    case ContractStatusEnum.Accepted: return "bg-green-100 text-green-800";
    case ContractStatusEnum.Closed: return "bg-green-100 text-green-800";
    default: return "bg-gray-100 text-gray-800";
  }
};

/**
 * Get milestone status text based on status ID
 */
export const getMilestoneStatusText = (statusId: number): string => {
  switch (Number(statusId)) {
    case MilestoneStatusEnum.Pending: return "Pending";
    case MilestoneStatusEnum.Active: return "Active";
    case MilestoneStatusEnum.Cancelled: return "Cancelled";
    case MilestoneStatusEnum.Completed: return "Completed";
    case MilestoneStatusEnum.Archived: return "Archived";
    case MilestoneStatusEnum.Suspended: return "Suspended";
    case MilestoneStatusEnum.Deleted: return "Deleted";
    case MilestoneStatusEnum.Paused: return "Paused";
    case MilestoneStatusEnum.Expired: return "Expired";
    case MilestoneStatusEnum.Rejected: return "Rejected";
    case MilestoneStatusEnum.Accepted: return "Accepted";
    default: return "Unknown";
  }
};

/**
 * Get milestone status color classes based on status ID
 */
export const getMilestoneStatusColor = (statusId: number): string => {
  switch (Number(statusId)) {
    case MilestoneStatusEnum.Pending: return "bg-yellow-100 text-yellow-800";
    case MilestoneStatusEnum.Active: return "bg-blue-100 text-blue-800";
    case MilestoneStatusEnum.Cancelled: return "bg-red-100 text-red-800";
    case MilestoneStatusEnum.Completed: return "bg-green-100 text-green-800";
    case MilestoneStatusEnum.Archived: return "bg-gray-100 text-gray-800";
    case MilestoneStatusEnum.Suspended: return "bg-orange-100 text-orange-800";
    case MilestoneStatusEnum.Deleted: return "bg-red-100 text-red-800";
    case MilestoneStatusEnum.Paused: return "bg-yellow-100 text-yellow-800";
    case MilestoneStatusEnum.Expired: return "bg-red-100 text-red-800";
    case MilestoneStatusEnum.Rejected: return "bg-red-100 text-red-800";
    case MilestoneStatusEnum.Accepted: return "bg-green-100 text-green-800";
    default: return "bg-gray-100 text-gray-800";
  }
};

/**
 * Get payment status text based on payment status string
 */
export const getPaymentStatusText = (paymentStatus: string): string => {
  switch (paymentStatus) {
    case "paid":
    case "funded":
    case "completed":
      return "Paid";
    case "pending":
    case "created":
      return "Pending Payment";
    case "failed":
    case "cancelled":
      return "Payment Failed";
    case "released":
      return "Released";
    case "no_payment":
      return "No Payment";
    default:
      return "No Payment";
  }
};

/**
 * Get payment status color classes based on payment status string
 */
export const getPaymentStatusColor = (paymentStatus: string): string => {
  switch (paymentStatus) {
    case "paid":
    case "funded":
    case "completed":
      return "bg-green-100 text-green-800";
    case "pending":
    case "created":
      return "bg-yellow-100 text-yellow-800";
    case "failed":
    case "cancelled":
      return "bg-red-100 text-red-800";
    case "released":
      return "bg-blue-100 text-blue-800";
    case "no_payment":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

/**
 * Check if contract can be accepted/declined based on order data
 */
export const canAcceptDeclineContract = (order: any, categorySettings?: any): boolean => {
  // Contract must be active (status 2)
  const isActive = Number(order.status) === ContractStatusEnum.Active;

  // Must have milestones
  const hasMilestones = order.milestones && order.milestones.length > 0;

  // Must be funded (payment status)
  const isFunded = order.paymentStatus === "paid" || order.paymentStatus === "funded";

  // Check if any milestone is not already processed
  const hasUnprocessedMilestone = order.milestones?.some((milestone: any) => {
    const statusId = Number(milestone.status?.id || milestone.status);
    return statusId !== MilestoneStatusEnum.Accepted && statusId !== MilestoneStatusEnum.Rejected;
  });

  // Check if contract response time has not expired
  const responseTimeNotExpired = !isContractResponseTimeExpired(order.createdAt, categorySettings);

  return isActive && hasMilestones && isFunded && hasUnprocessedMilestone && responseTimeNotExpired;
};

/**
 * Get contract response time status message
 */
export const getContractResponseTimeStatus = (order: any, categorySettings?: any): {
  canRespond: boolean;
  message: string;
  hoursRemaining?: number;
} => {
  const responseTimeExpired = isContractResponseTimeExpired(order.createdAt, categorySettings);
  
  if (responseTimeExpired) {
    return {
      canRespond: false,
      message: "Contract response time has expired. You can no longer accept or decline this contract."
    };
  }
  
  // Calculate hours remaining
  const createdDate = new Date(order.createdAt);
  const currentDate = new Date();
  const hoursDiff = (currentDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60);
  const responseTimeHours = categorySettings?.contractResponseTime || 0;
  const hoursRemaining = Math.max(0, responseTimeHours - hoursDiff);
  
  return {
    canRespond: true,
    message: `You have ${Math.ceil(hoursRemaining)} hours remaining to respond to this contract.`,
    hoursRemaining: Math.ceil(hoursRemaining)
  };
};

/**
 * Check if contract requires Smart Work Tracker checkbox
 */
export const requiresTrackerCheckbox = (order: any): boolean => {
  return order.isHourly && order.enableTracker === true;
};

/**
 * Default contract action handler (can be overridden)
 */
export const defaultHandleContractAction = async (
  contractId: string, 
  action: "accept" | "decline",
  contracts?: any[],
  onSuccess?: (contractId: string, action: string) => void
): Promise<void> => {
  try {
    console.log(`Contract ${action} action for contract:`, contractId);
    
    // Find the contract
    const contract = contracts?.find(c => c.id === contractId);
    if (!contract) {
      console.error("Contract not found:", contractId);
      return;
    }

    // Here you would typically call your API endpoints
    // For now, we'll just show a success message
    alert(`Contract ${action === "accept" ? "accepted" : "declined"} successfully!`);
    
    // Call success callback if provided
    if (onSuccess) {
      onSuccess(contractId, action);
    }
  } catch (error) {
    console.error("Error handling contract action:", error);
    alert("Failed to update contract. Please try again.");
  }
};

/**
 * Default payment release request handler (can be overridden)
 */
export const defaultHandleRequestPaymentRelease = async (
  milestoneId: string,
  onSuccess?: (milestoneId: string) => void
): Promise<void> => {
  try {
    console.log("Request payment release for milestone:", milestoneId);
    
    // Here you would typically call your API endpoint
    // For now, we'll just show a success message
    alert("Payment release requested successfully!");
    
    // Call success callback if provided
    if (onSuccess) {
      onSuccess(milestoneId);
    }
  } catch (error) {
    console.error("Error requesting payment release:", error);
    alert("Failed to request payment release. Please try again.");
  }
};

/**
 * Default tracker checkbox change handler (can be overridden)
 */
export const defaultHandleTrackerCheckboxChange = (
  contractId: string,
  checked: boolean,
  setTrackerCheckboxStates?: React.Dispatch<React.SetStateAction<{ [contractId: string]: boolean }>>
): void => {
  console.log("Tracker checkbox changed:", contractId, checked);
  
  if (setTrackerCheckboxStates) {
    setTrackerCheckboxStates(prev => ({
      ...prev,
      [contractId]: checked
    }));
  }
};





